// libs
import * as React from 'react'

// interfaces / constants

// classes / components
import Notification, {
  clsHighlightedText,
  clsText,
  NotificationProps
} from 'src/components/notifications/notification'

// helpers
import Translate, { textResources } from 'src/lang/de'
import DateFormatter from 'src/utils/date_formatter/date_formatter'

// images
import { NotificationsQuery, ReactionName } from '@lopo-tech/lopo-api'
import * as reactionCandle from 'src/components/notifications/badges/Candle@2x.png'
import * as reactionCandleActive from 'src/components/notifications/badges/CandleActive@2x.png'
import * as reactionHaha from 'src/components/notifications/badges/Haha@2x.png'
import * as reactionHahaActive from 'src/components/notifications/badges/HahaActive@2x.png'
import * as reactionInteresting from 'src/components/notifications/badges/Interessant@2x.png'
import * as reactionInterestingActive from 'src/components/notifications/badges/InteressantActive@2x.png'
import * as reactionLike from 'src/components/notifications/badges/Like@2x.png'
import * as reactionLikeActive from 'src/components/notifications/badges/LikeActive@2x.png'
import * as reactionOhNo from 'src/components/notifications/badges/OhNein@2x.png'
import * as reactionOhNoActive from 'src/components/notifications/badges/OhNeinActive@2x.png'
import * as reactionThumbsDown from 'src/components/notifications/badges/ThumbsDown@2x.png'
import * as reactionThumbsDownActive from 'src/components/notifications/badges/ThumbsDownActive@2x.png'
import * as reactionThumbsUp from 'src/components/notifications/badges/ThumbsUp@2x.png'
import * as reactionThumbsUpActive from 'src/components/notifications/badges/ThumbsUpActive@2x.png'
import * as reactionWow from 'src/components/notifications/badges/Wow@2x.png'
import * as reactionWowActive from 'src/components/notifications/badges/WowActive@2x.png'

const labels = textResources.notification

const badgeImages: Record<ReactionName, string | undefined> = {
  [ReactionName.Candle]: reactionCandleActive,
  [ReactionName.Haha]: reactionHahaActive,
  [ReactionName.Interesting]: reactionInterestingActive,
  [ReactionName.Like]: reactionLikeActive,
  [ReactionName.OhNo]: reactionOhNoActive,
  [ReactionName.OldHeart]: reactionLikeActive,
  [ReactionName.Wow]: reactionWowActive,
  [ReactionName.ThumbsDown]: reactionThumbsDownActive,
  [ReactionName.ThumbsUp]: reactionThumbsUpActive
}

const readBadgeImages: Record<ReactionName, string | undefined> = {
  [ReactionName.Candle]: reactionCandle,
  [ReactionName.Haha]: reactionHaha,
  [ReactionName.Interesting]: reactionInteresting,
  [ReactionName.Like]: reactionLike,
  [ReactionName.OhNo]: reactionOhNo,
  [ReactionName.OldHeart]: reactionLike,
  [ReactionName.Wow]: reactionWow,
  [ReactionName.ThumbsDown]: reactionThumbsDown,
  [ReactionName.ThumbsUp]: reactionThumbsUp
}

type Notification = NotificationsQuery['notifications']['items'][number];
type NotificationReactable = Extract<
  Notification,
  { __typename?: 'NewReactionNotification' }
>['reactable'];

export type BookmarkNotificationProps = {
  reactable: NotificationReactable;
  createdAt: string;
  type: ReactionName;
} & Pick<
  NotificationProps,
  'avatarImage' | 'initiatorName' | 'isRead' | 'onClick'
>;

const BookmarkNotification: React.FC<BookmarkNotificationProps> = ({
  avatarImage,
  createdAt,
  initiatorName,
  isRead,
  onClick,
  reactable,
  type
}) => {
  const bookmarkable =
    Translate.notifications.contentType[reactable.__typename]
  const teaser = deriveReactionTeaser(reactable)

  return (
    <Notification
      avatarImage={avatarImage}
      badgeImageRead={readBadgeImages[type]}
      badgeImageNotRead={badgeImages[type]}
      footer={DateFormatter.asTimeAgo(createdAt)}
      initiatorName={initiatorName}
      isRead={isRead}
      onClick={onClick}
    >
      <span
        className={clsText}
      >{` ${labels.notificationBookmark} ${bookmarkable}`}</span>
      {teaser && <strong className={clsHighlightedText}>{teaser}</strong>}
      <span className={clsText}>{` ${labels.notificationReacted}`}</span>
    </Notification>
  )
}

const getTeaser = (title: string | null | undefined) => {
  return title ? ` „${title}“ ` : ' '
}

const deriveReactionTeaser = (reactable: NotificationReactable) => {
  switch (reactable.__typename) {
    case 'Comment':
      return getTeaser(reactable.message)
    case 'Article':
    case 'Event':
    case 'Snapshot':
      return getTeaser(reactable.title)
    default:
      return getTeaser(undefined)
  }
}

export default BookmarkNotification
